import { ItemType } from '@/features/Claim/types';
import { useGetShipmentDetailsQuery } from '@/hooks/queries/useGetShipmentDetailsQuery';
import { InsuranceState } from '@typings/insurance';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

export const useClaimShipmentProvider = (shipmentId: string) => {
  const { data: shipment, isLoading } = useGetShipmentDetailsQuery({
    shipmentId,
  });

  const { formatNumber } = useIntl();

  const formatCurrency = useCallback(
    (value: number, currency: string | null) => {
      return formatNumber(value, {
        style: 'currency',
        currency: currency || '',
        minimumFractionDigits: 2,
      });
    },
    [formatNumber]
  );

  const items: ItemType[] = useMemo(
    () =>
      !shipment?.parcelsAttributes
        ? []
        : shipment.parcelsAttributes.flatMap(({ shipmentItemsAttributes }) =>
            shipmentItemsAttributes.map(
              ({ id, description, declaredCurrency, declaredCustomsValue, quantity }) => {
                const amount = formatCurrency(declaredCustomsValue || 0, declaredCurrency);
                const pcs = quantity > 1 ? 'pcs' : 'pc';
                const totalAmount = formatCurrency(
                  (declaredCustomsValue || 0) * quantity,
                  declaredCurrency
                );

                return {
                  itemId: id,
                  description: `${description} (${amount} x ${quantity}${pcs} - ${totalAmount})`,
                  quantity,
                } as ItemType;
              }
            )
          ),
    [shipment, formatCurrency]
  );

  const insuredState = useMemo((): InsuranceState => {
    const courierOffersLiability =
      shipment?.courier?.insuranceLiability &&
      !/no\s?liability/i.test(shipment?.courier?.insuranceLiability);

    const insuranceFee =
      typeof shipment?.insuranceFee === 'string'
        ? parseFloat(shipment?.insuranceFee)
        : shipment?.insuranceFee;

    // If applied through InsureShip
    if (shipment?.isInsured) {
      if (shipment.insuranceRequestedByBuyer) return 'buyer-insured';
      if (insuranceFee) return 'self-insured';
      return courierOffersLiability ? 'fallback-courier' : 'fallback-uninsured';
    }

    if (courierOffersLiability) return 'courier-insured';

    return shipment?.courier && !shipment?.courier.isEasyshipCourier
      ? 'lyoc-uninsured'
      : 'uninsured';
  }, [shipment]);

  return {
    shipment,
    isLoading,
    items,
    insuredState,
  };
};

type ClaimShipmentContextType = ReturnType<typeof useClaimShipmentProvider>;

export const ClaimShipmentContext = createContext<ClaimShipmentContextType>(
  {} as ClaimShipmentContextType
);

export const useClaimShipmentContext = () => {
  return useContext(ClaimShipmentContext);
};
